import React, { Suspense, lazy } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { auth } from '@/lib/api';
import useHotKeys from '@/hooks/useHotKeys';

// ── Eager imports ───────────────────────────────────────────────
// Keep the initial-bundle surface minimal. Only include:
//   • Public-crawlable pages   (Landing, Login, Trust*, AuthCallback)
//   • Global chrome            (AppShell, CommandPalette, Onboarding,
//                               PWA prompt, NotificationWorker)
// Everything else is React.lazy'd below so anonymous visitors and
// SEO crawlers don't pay the byte-cost of admin/authed screens.
import Login from '@/components/Login';
import CommandPalette from '@/components/CommandPalette';
import OnboardingTour from '@/components/OnboardingTour';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import TrustPage from '@/components/TrustPage';
import AuthCallback from '@/components/AuthCallback';
import NotificationWorker from '@/components/NotificationWorker';
import AppShell from '@/components/navigation/AppShell';
import SessionExpiryGuard from '@/components/SessionExpiryGuard';
import Landing from '@/pages/Landing';
import TrustSpreads from '@/pages/TrustSpreads';

// ── Lazy-loaded authed/admin routes ─────────────────────────────
// One dynamic import per route == one webpack chunk. First
// navigation to a given screen fetches its chunk on demand; the
// initial `main.js` no longer carries any of this code.
// `lazyRetry` guards against stale-deploy chunk 404s: if a chunk
// fails to load (old index.html referencing purged hashed files
// after a redeploy), force ONE full reload to pick up the new build
// instead of rendering a blank screen.
const lazyRetry = (importer) => lazy(() =>
  importer().catch((err) => {
    const key = 'apex_chunk_reload';
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      window.location.reload();
      return new Promise(() => {});
    }
    sessionStorage.removeItem(key);
    throw err;
  })
);
const Dashboard = lazyRetry(() => import('@/components/Dashboard'));
const DashboardV2 = lazyRetry(() => import('@/components/DashboardV2'));
const Compliance = lazyRetry(() => import('@/components/Compliance'));
const GovernanceConsole = lazyRetry(() => import('@/components/GovernanceConsole'));
const ResetPassword = lazyRetry(() => import('@/components/ResetPassword'));
const TrustedLocation = lazyRetry(() => import('@/components/TrustedLocation'));
const Settings = lazyRetry(() => import('@/components/Settings'));
const Strategies = lazyRetry(() => import('@/components/Strategies'));
const StrategyDetail = lazyRetry(() => import('@/components/StrategyDetail'));
const PortfolioLab = lazyRetry(() => import('@/components/PortfolioLab'));
const VenuesLab = lazyRetry(() => import('@/components/VenuesLab'));
const Marketplace = lazyRetry(() => import('@/components/Marketplace'));
const Admin = lazyRetry(() => import('@/components/Admin'));
const Banking = lazyRetry(() => import('@/components/Banking'));
const BankingSuccess = lazyRetry(() => import('@/components/BankingSuccess'));
const TradingHistory = lazyRetry(() => import('@/pages/TradingHistory'));
const Analytics = lazyRetry(() => import('@/pages/Analytics'));
const Reports = lazyRetry(() => import('@/pages/Reports'));
const CsvDownloads = lazyRetry(() => import('@/pages/CsvDownloads'));
const Risk = lazyRetry(() => import('@/pages/Risk'));
const Payouts = lazyRetry(() => import('@/pages/Payouts'));
const ProfitEngine = lazyRetry(() => import('@/pages/ProfitEngine'));
const PromotionConsole = lazyRetry(() => import('@/pages/PromotionConsole'));
const ExchangeHealth = lazyRetry(() => import('@/pages/ExchangeHealth'));
const CapitalPreservationConsole = lazyRetry(() => import('@/pages/CapitalPreservationConsole'));
const ExecutiveBrief = lazyRetry(() => import('@/pages/ExecutiveBrief'));
const BriefingHistory = lazyRetry(() => import('@/pages/BriefingHistory'));
const ComplianceVault = lazyRetry(() => import('@/pages/ComplianceVault'));
const BayesianGateTracker = lazyRetry(() => import('@/pages/BayesianGateTracker'));
const Phase5MasterDashboard = lazyRetry(() => import('@/pages/Phase5MasterDashboard'));
const AuditorSigningCeremony = lazyRetry(() => import('@/pages/AuditorSigningCeremony'));
const SoakMonitor = lazyRetry(() => import('@/pages/SoakMonitor'));
const ProfitabilityCommandCenter = lazyRetry(() => import('@/pages/ProfitabilityCommandCenter'));


// Lightweight suspense fallback — mono-typography spinner matching
// the app's design language. Rendered per route while its chunk
// is in-flight (~50-300 ms on first visit, ~0 ms after HTTP cache).
function RouteFallback() {
  return (
    <div
      data-testid="route-suspense-fallback"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '60vh',
        fontFamily: 'JetBrains Mono, ui-monospace, monospace',
        color: '#71717a', fontSize: 11, letterSpacing: 1.5,
        textTransform: 'uppercase',
      }}>
      Loading module…
    </div>
  );
}

// PublicOrDashboard — root route dispatcher.
//   • Unauthed users see the SEO-friendly Landing page.
//   • Authed users are redirected to the authenticated dashboard.
// Keeps the gated app intact while making `/` crawlable.
function PublicOrDashboard() {
  if (auth.isAuthed()) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

function RequireAuth({ children, role }) {
  if (!auth.isAuthed()) return <Navigate to="/login" replace />;
  // Server-side guards remain; this is a frontend convenience redirect
  // for direct-URL hits to admin-only routes by non-admin users.
  if (role) {
    const u = auth.currentUser();
    if (u?.role !== role) return <Navigate to="/" replace />;
  }
  return (
    <AppShell>
      <Suspense fallback={<RouteFallback />}>
        {children}
      </Suspense>
      <NotificationWorker />
      <PWAInstallPrompt />
    </AppShell>
  );
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = auth.currentUser()?.role === 'admin';
  // Power-trader keyboard shortcuts — registered once for the whole
  // app. See `hooks/useHotKeys.js` for the binding map. Skips inputs
  // and disabled-when-typing automatically.
  useHotKeys({ navigate, isAdmin });
  // Handle OAuth callback synchronously (URL fragment cannot be inspected in useEffect in time)
  if (location.hash?.includes('session_id=') || location.hash?.includes('apex_token=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/trust" element={<TrustPage />} />
      <Route path="/" element={<PublicOrDashboard />} />
      <Route path="/dashboard" element={<RequireAuth><DashboardV2 /></RequireAuth>} />
      <Route path="/dashboard-v2" element={<RequireAuth><DashboardV2 /></RequireAuth>} />
      <Route path="/dashboard-legacy" element={<RequireAuth><Dashboard /></RequireAuth>} />
      <Route path="/compliance" element={<RequireAuth role="admin"><Compliance /></RequireAuth>} />
      <Route path="/governance" element={<RequireAuth role="admin"><GovernanceConsole /></RequireAuth>} />
      <Route path="/trusted-location" element={<RequireAuth role="admin"><TrustedLocation /></RequireAuth>} />
      <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
      <Route path="/strategies" element={<RequireAuth><Strategies /></RequireAuth>} />
      <Route path="/strategies/:sid" element={<RequireAuth><StrategyDetail /></RequireAuth>} />
      <Route path="/portfolio" element={<RequireAuth><PortfolioLab /></RequireAuth>} />
      <Route path="/venues" element={<RequireAuth><VenuesLab /></RequireAuth>} />
      <Route path="/marketplace" element={<RequireAuth><Marketplace /></RequireAuth>} />
      <Route path="/admin" element={<RequireAuth role="admin"><Admin /></RequireAuth>} />
      <Route path="/banking" element={<RequireAuth><Banking /></RequireAuth>} />
      <Route path="/banking/success" element={<RequireAuth><BankingSuccess /></RequireAuth>} />
      <Route path="/trading-history" element={<RequireAuth><TradingHistory /></RequireAuth>} />
      <Route path="/analytics" element={<RequireAuth><Analytics /></RequireAuth>} />
      <Route path="/reports" element={<RequireAuth><Reports /></RequireAuth>} />
      <Route path="/csv-downloads" element={<RequireAuth><CsvDownloads /></RequireAuth>} />
      <Route path="/risk" element={<RequireAuth><Risk /></RequireAuth>} />
      <Route path="/payouts" element={<RequireAuth><Payouts /></RequireAuth>} />
      <Route path="/profit-engine" element={<RequireAuth><ProfitEngine /></RequireAuth>} />
      <Route path="/promotion-console" element={<RequireAuth role="admin"><PromotionConsole /></RequireAuth>} />
      <Route path="/exchange-health" element={<RequireAuth><ExchangeHealth /></RequireAuth>} />
      <Route path="/capital-preservation" element={<RequireAuth role="admin"><CapitalPreservationConsole /></RequireAuth>} />
      <Route path="/executive-brief" element={<RequireAuth><ExecutiveBrief /></RequireAuth>} />
      <Route path="/executive-brief/history" element={<RequireAuth role="admin"><BriefingHistory /></RequireAuth>} />
      <Route path="/compliance-vault" element={<RequireAuth role="admin"><ComplianceVault /></RequireAuth>} />
      <Route path="/bayesian-gate-tracker" element={<RequireAuth role="admin"><BayesianGateTracker /></RequireAuth>} />
      <Route path="/phase5-dashboard" element={<RequireAuth role="admin"><Phase5MasterDashboard /></RequireAuth>} />
      <Route path="/auditor-signing-ceremony" element={<RequireAuth role="admin"><AuditorSigningCeremony /></RequireAuth>} />
      <Route path="/soak-monitor" element={<RequireAuth><SoakMonitor /></RequireAuth>} />
      <Route path="/profitability" element={<RequireAuth role="admin"><ProfitabilityCommandCenter /></RequireAuth>} />
      <Route path="/trust/spreads" element={<TrustSpreads />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <CommandPalette />
        <OnboardingTour />
        <SessionExpiryGuard />
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
